import React, { useState } from 'react';
import { Row, Col, Table, Button, Modal, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../shared/api';
import NumberInput from '../../components/form/NumberInput';
import { getContrastColor, COLOR_PRIMARY, COLOR_SUCCESS, COLOR_SECONDARY, COLOR_INFO } from '../../shared/colorUtils';
import { useConfirm } from '../../shared/ConfirmContext';
import { IconPlus } from '@tabler/icons-react';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import AppButton from '../../components/ui/AppButton';
import { CenteredLoader } from '../../components/LoadingSpinner';

const TariffTypes = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    unit: '',
    multiplier: 1.0,
    is_time_based: true,
    is_active: true,
    order: 0,
    color: '#066fd1'
  });

  const { data: tariffTypes = [], isLoading: loading } = useQuery({
    queryKey: ['tariff-types'],
    queryFn: () => api.get('settings/api/tariff-types/').then(d => Array.isArray(d) ? d : []),
  });

  const saveMutation = useMutation({
    mutationFn: (payload) => editingType
      ? api.put(`settings/api/tariff-types/${editingType.id}/`, payload)
      : api.post('settings/api/tariff-types/', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tariff-types'] });
      handleCloseModal();
    },
    onError: (err) => setError(err.message || 'Nepodařilo se uložit typ tarifu'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`settings/api/tariff-types/${id}/`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tariff-types'] }),
    onError: (err) => setError(err.message || 'Nepodařilo se smazat typ tarifu'),
  });

  const reorderMutation = useMutation({
    mutationFn: (reordered) => Promise.all(
      reordered.map((item, index) =>
        api.patch(`settings/api/tariff-types/${item.id}/`, { order: (index + 1) * 10 })
      )
    ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tariff-types'] }),
    onError: () => queryClient.invalidateQueries({ queryKey: ['tariff-types'] }),
  });

  const handleShowModal = (type = null) => {
    if (type) {
      setEditingType(type);
      setFormData({
        name: type.name,
        code: type.code,
        description: type.description || '',
        unit: type.unit,
        multiplier: type.multiplier,
        is_time_based: type.is_time_based,
        is_active: type.is_active,
        order: type.order,
        color: type.color
      });
    } else {
      setEditingType(null);
      setFormData({
        name: '',
        code: '',
        description: '',
        unit: '',
        multiplier: 1.0,
        is_time_based: true,
        is_active: true,
        order: 0,
        color: '#066fd1'
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingType(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    saveMutation.mutate(formData);
  };

  const handleDelete = async (id) => {
    if (!await confirm(t('settings.tariffTypes.deleteConfirm'))) return;
    deleteMutation.mutate(id);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    const reordered = Array.from(tariffTypes);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    queryClient.setQueryData(['tariff-types'], reordered);
    reorderMutation.mutate(reordered);
  };

  if (loading) {
    return (
      <PageShell pretitle={t('settings.overview')} title={t('settings.tariffTypes.title')}>
        <CenteredLoader />
      </PageShell>
    );
  }

  return (
    <PageShell
      pretitle={t('settings.overview')}
      title={t('settings.tariffTypes.title')}
      actions={
        <AppButton variant="primary" icon={<IconPlus size={16} />} onClick={() => handleShowModal()}>
          {t('settings.tariffTypes.add')}
        </AppButton>
      }
    >
      {error && <div className="alert alert-danger app-inline-alert" role="alert">{error}</div>}

      <PageSection bodyClassName="p-0">
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Table responsive hover className="mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: '32px' }}></th>
                          <th>Pořadí</th>
                          <th>Název</th>
                          <th>Kód</th>
                          <th>Jednotka</th>
                          <th>Multiplikátor</th>
                          <th>Typ</th>
                          <th>Stav</th>
                          <th>Tarifů</th>
                          <th>Akce</th>
                        </tr>
                      </thead>
                      <Droppable droppableId="tariff-types">
                        {(provided) => (
                          <tbody ref={provided.innerRef} {...provided.droppableProps}>
                            {tariffTypes.map((type, index) => (
                              <Draggable
                                key={`tt-${type.id}`}
                                draggableId={`tt-${type.id}`}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <tr
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    style={{
                                      ...provided.draggableProps.style,
                                      boxShadow: snapshot.isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
                                      background: snapshot.isDragging ? 'var(--tblr-bg-surface-secondary)' : '',
                                    }}
                                  >
                                    <td
                                      {...provided.dragHandleProps}
                                      className="text-muted text-center"
                                      style={{ cursor: snapshot.isDragging ? 'grabbing' : 'grab', verticalAlign: 'middle' }}
                                      title="Přetáhnout pro změnu pořadí"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                        <circle cx="9" cy="12" r="1"></circle>
                                        <circle cx="9" cy="7" r="1"></circle>
                                        <circle cx="9" cy="17" r="1"></circle>
                                        <circle cx="15" cy="12" r="1"></circle>
                                        <circle cx="15" cy="7" r="1"></circle>
                                        <circle cx="15" cy="17" r="1"></circle>
                                      </svg>
                                    </td>
                                    <td>{type.order}</td>
                                    <td>
                                      <span
                                        className="badge me-2"
                                        style={{ backgroundColor: type.color, color: getContrastColor(type.color) }}
                                      >
                                        {type.name}
                                      </span>
                                    </td>
                                    <td><code>{type.code}</code></td>
                                    <td>{type.unit}</td>
                                    <td>{type.multiplier}</td>
                                    <td>
                                      {type.is_time_based ? (
                                        <span className="badge" style={{ backgroundColor: COLOR_INFO, color: getContrastColor(COLOR_INFO) }}>Časový</span>
                                      ) : (
                                        <span className="badge" style={{ backgroundColor: COLOR_SECONDARY, color: getContrastColor(COLOR_SECONDARY) }}>Ostatní</span>
                                      )}
                                    </td>
                                    <td>
                                      {type.is_active ? (
                                        <span className="badge" style={{ backgroundColor: COLOR_SUCCESS, color: getContrastColor(COLOR_SUCCESS) }}>Aktivní</span>
                                      ) : (
                                        <span className="badge" style={{ backgroundColor: COLOR_SECONDARY, color: getContrastColor(COLOR_SECONDARY) }}>Neaktivní</span>
                                      )}
                                      {type.is_system && (
                                        <span className="badge ms-1" style={{ backgroundColor: COLOR_PRIMARY, color: getContrastColor(COLOR_PRIMARY) }}>Systémový</span>
                                      )}
                                    </td>
                                    <td>{type.tariff_count || 0}</td>
                                    <td>
                                      <div className="btn-group">
                                        <button
                                          type="button"
                                          className="btn btn-outline-primary btn-sm"
                                          title="Upravit"
                                          onClick={() => handleShowModal(type)}
                                        >
                                          <i className="ti ti-edit"></i>
                                        </button>
                                        {!type.is_system && (
                                          <button
                                            type="button"
                                            className="btn btn-outline-danger btn-sm"
                                            title="Smazat"
                                            onClick={() => handleDelete(type.id)}
                                          >
                                            <i className="ti ti-trash"></i>
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </tbody>
                        )}
                      </Droppable>
                    </Table>
                  </DragDropContext>
      </PageSection>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" fullscreen="sm-down">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingType ? 'Upravit typ tarifu' : 'Přidat typ tarifu'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Název *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Kód *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    required
                    disabled={editingType?.is_system}
                  />
                  <Form.Text className="text-muted">
                    Pouze malá písmena, číslice a podtržítka
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Jednotka *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Multiplikátor</Form.Label>
                  <NumberInput
                    step="any"
                    value={formData.multiplier}
                    onChange={(multiplier) => setFormData({ ...formData, multiplier })}
                    required
                  />
                  <Form.Text className="text-muted">
                    Koeficient pro přepočet na základní jednotku
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Pořadí</Form.Label>
                  <NumberInput
                    step="any"
                    value={formData.order}
                    onChange={(order) => setFormData({ ...formData, order })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Barva</Form.Label>
                  <Form.Control
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Popis</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  label="Založeno na čase"
                  checked={formData.is_time_based}
                  onChange={(e) => setFormData({ ...formData, is_time_based: e.target.checked })}
                />
              </Col>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  label="Aktivní"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Zrušit
            </Button>
            <Button variant="primary" type="submit">
              {editingType ? 'Uložit změny' : 'Přidat'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </PageShell>
  );
};

export default TariffTypes;
